import { useCallback, useMemo, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
//
import {
  Alert,
  Autocomplete,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  VBMActionButtons,
  VBMCardTitle,
  VBMCardWrapper,
  VBMEditButton,
} from '..';
// API
import {
  getActiveProviders,
  getProvidersTimeOff,
} from '@/features/users/shared/api';
import {
  acceptBookingSessionCleaner,
  getBookingSession,
  getSchedulerBookingSessions,
} from '@/features/bookings-management/api';
// hooks + utils
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import usePostBookingSessionUpdate from '@/features/bookings-management/hooks/usePostBookingSessionUpdate';
import { providersPath } from '@/features/users/providers';
// types
import { BookingUser } from '@/features/bookings-management/types';
import { resolveSessionRange } from '@/features/bookings-management/utils';
import { ProviderTimeOff } from '@/features/users/shared/types';
import { parseISO } from 'date-fns';
import { fCurrency } from '@/utils/formatNumber';

type VBMCleanerProps = {
  selectedOption?: BookingUser | null;
  editable?: boolean;
  providerPricePerHour?: number | string | null;
  providerIncome?: number | string | null;
};

export default function VBMCleaner({
  selectedOption,
  editable = false,
  providerPricePerHour,
  providerIncome,
}: VBMCleanerProps) {
  const [isEdit, setIsEdit] = useState(false);
  const [isProviderRateDialogOpen, setProviderRateDialogOpen] = useState(false);
  const [providerRateInput, setProviderRateInput] = useState('');
  const [providerRateErrorText, setProviderRateErrorText] = useState('');
  const [selectedCleanerId, setSelectedCleanerId] = useState<number | null>(
    null
  );
  const [restrictionWarningMessages, setRestrictionWarningMessages] = useState<
    string[]
  >([]);
  const [searchParams] = useSearchParams();
  const sessionID = searchParams.get('sessionID');
  const queryClient = useQueryClient();
  const { errorMsg, successMsg } = useSnackbarMsg();

  const { data: providers, isLoading: isProvidersDataLoading } = useQuery({
    queryKey: ['providers', 'active'],
    queryFn: () => getActiveProviders(),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching providers`);
    },
    refetchOnWindowFocus: false,
  });

  const { sessionUpdateMutation } = usePostBookingSessionUpdate({
    onSuccessFn: () => {
      setIsEdit(false);
      setSelectedCleanerId(null);
    },
    successText: `Cleaner updated`,
    warningCleaners: providers,
    onRestrictionWarningMessages: setRestrictionWarningMessages,
  });
  const { sessionUpdateMutation: providerRateUpdateMutation } =
    usePostBookingSessionUpdate({
      onSuccessFn: () => setProviderRateDialogOpen(false),
      successText: `Provider rate updated`,
    });
  const providerAcceptanceUpdateMutation = useMutation({
    mutationFn: ({
      bookingSessionId,
      cleanerId,
    }: {
      bookingSessionId: number;
      cleanerId: number;
    }) => acceptBookingSessionCleaner(bookingSessionId, cleanerId),
    onSuccess: () => {
      successMsg('Cleaner marked accepted');
      if (sessionID) {
        queryClient.invalidateQueries(['bookingSession', sessionID]);
      }
      queryClient.invalidateQueries(['bookingSessions']);
    },
    onError: (error) => {
      errorMsg(error, 'Unable to mark cleaner accepted');
    },
  });

  const handleToggleEdit = (boolean: boolean) => {
    if (boolean) {
      setRestrictionWarningMessages([]);
      setSelectedCleanerId(null);
    }
    setIsEdit(boolean);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    sessionUpdateMutation.mutate({
      bookingSessionId: Number(sessionID),
      bookingSessionDto: {
        booking_cleaners: selectedCleanerId ? [Number(selectedCleanerId)] : [],
      },
    });
  };

  const getProviderRateInputValue = () => {
    if (
      providerPricePerHour === null ||
      providerPricePerHour === undefined ||
      providerPricePerHour === ''
    ) {
      return '';
    }

    const normalizedValue =
      typeof providerPricePerHour === 'string'
        ? providerPricePerHour.trim()
        : providerPricePerHour;
    const numericValue = Number(normalizedValue);

    return Number.isFinite(numericValue) ? String(normalizedValue) : '';
  };

  const handleOpenProviderRateDialog = () => {
    setProviderRateInput(getProviderRateInputValue());
    setProviderRateErrorText('');
    setProviderRateDialogOpen(true);
  };

  const handleCloseProviderRateDialog = () => {
    if (providerRateUpdateMutation.isLoading) return;
    setProviderRateDialogOpen(false);
  };

  const handleSubmitProviderRate = () => {
    const trimmedValue = providerRateInput.trim();

    if (!trimmedValue) {
      setProviderRateErrorText('Enter a provider rate.');
      return;
    }

    const parsedRate = Number(trimmedValue);

    if (!Number.isFinite(parsedRate)) {
      setProviderRateErrorText('Enter a valid provider rate.');
      return;
    }

    if (parsedRate < 0) {
      setProviderRateErrorText('Provider rate cannot be negative.');
      return;
    }

    const bookingSessionId = Number(sessionID);

    if (
      !sessionID ||
      !Number.isFinite(bookingSessionId) ||
      bookingSessionId <= 0
    ) {
      setProviderRateErrorText('Booking session is missing.');
      return;
    }

    providerRateUpdateMutation.mutate({
      bookingSessionId,
      bookingSessionDto: { provider_price_per_hour: parsedRate },
    });
  };

  const handleMarkProviderAccepted = () => {
    const bookingSessionId = Number(sessionID);

    if (
      !sessionID ||
      !Number.isFinite(bookingSessionId) ||
      bookingSessionId <= 0
    ) {
      errorMsg(
        'Booking session is missing.',
        'Unable to mark cleaner accepted'
      );
      return;
    }

    if (!selectedOption?.id) {
      errorMsg('No cleaner assigned.', 'Unable to mark cleaner accepted');
      return;
    }

    providerAcceptanceUpdateMutation.mutate({
      bookingSessionId,
      cleanerId: Number(selectedOption.id),
    });
  };

  const { data: bookingSession, isLoading: isBookingSessionLoading } = useQuery(
    {
      queryKey: ['bookingSession', sessionID],
      queryFn: () => getBookingSession(Number(sessionID)),
      enabled: !!sessionID,
      refetchInterval: 5000, // refresh every 5 seconds
      onError: (error) => errorMsg(error, 'Unable to fetch booking session'),
    }
  );

  const sessionDate = bookingSession?.date;
  const sessionDayQueryEnabled = Boolean(sessionDate);

  const { data: daySessions } = useQuery({
    queryKey: ['schedulerSessions', sessionDate],
    queryFn: () =>
      getSchedulerBookingSessions(
        `?startDate=${sessionDate}&endDate=${sessionDate}`
      ),
    enabled: sessionDayQueryEnabled,
    onError: (error) => errorMsg(error, 'Unable to fetch cleaner workload'),
  });

  const { data: providersTimeOff } = useQuery<ProviderTimeOff[]>({
    queryKey: ['providersTimeOff', sessionDate],
    queryFn: () =>
      getProvidersTimeOff({
        startDate: sessionDate,
        endDate: sessionDate,
      }),
    enabled: sessionDayQueryEnabled,
  });

  const targetRange = useMemo(
    () => resolveSessionRange(bookingSession),
    [bookingSession]
  );

  const isProviderAvailable = useCallback(
    (providerId: number) => {
      if (!targetRange) return true;
      const { start: targetStart, end: targetEnd } = targetRange;

      // Check other assigned sessions
      const overlappingSessions = (daySessions || []).filter((session) => {
        if (session.id === bookingSession?.id) return false;
        const cleanerIds = session.booking_session_cleaners?.map((cleaner) => {
          const directId = (cleaner as BookingUser).id;
          if (directId) return directId;
          // fallback for legacy shape that nests user details under `users`
          return (cleaner as any)?.users?.id ?? null;
        });
        if (!cleanerIds?.includes(providerId)) return false;

        const sessionRange = resolveSessionRange(session);
        if (!sessionRange) return false;

        const { start, end } = sessionRange;
        return start < targetEnd && end > targetStart;
      });

      if (overlappingSessions.length) return false;

      // Check time off
      const overlappingTimeOff = (providersTimeOff || []).some((timeOff) => {
        if (timeOff.provider?.id !== providerId) return false;
        const timeOffStart = timeOff.start_date
          ? parseISO(timeOff.start_date)
          : null;
        const timeOffEnd = timeOff.end_date ? parseISO(timeOff.end_date) : null;
        if (!timeOffStart || !timeOffEnd) return false;
        return timeOffStart < targetEnd && timeOffEnd > targetStart;
      });

      return !overlappingTimeOff;
    },
    [bookingSession?.id, daySessions, providersTimeOff, targetRange]
  );

  const filteredProviders = useMemo(() => {
    if (!providers) return [];
    if (!targetRange) return providers;

    return providers.filter((provider) => {
      if (!provider?.id) return false;
      // Always include current cleaner even if overlapping
      if (selectedOption?.id && provider.id === selectedOption.id) {
        return true;
      }
      return isProviderAvailable(provider.id);
    });
  }, [providers, selectedOption?.id, targetRange, isProviderAvailable]);

  const formatProviderPayValue = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') return 'N/A';

    const normalizedValue = typeof value === 'string' ? value.trim() : value;
    if (normalizedValue === '') return 'N/A';

    const numericValue = Number(normalizedValue);
    return Number.isFinite(numericValue) ? fCurrency(numericValue) : 'N/A';
  };

  const providerRateValue = formatProviderPayValue(providerPricePerHour);
  const providerIncomeValue = formatProviderPayValue(providerIncome);
  const providerRateDisplay =
    providerRateValue === 'N/A' ? providerRateValue : `${providerRateValue}/hr`;
  const providerAcceptanceDisplay = selectedOption?.accepted_by_provider
    ? 'Accepted'
    : 'Not accepted';

  const renderCleaner = () =>
    selectedOption ? (
      <Chip
        component={RouterLink}
        to={providersPath('edit', {
          userId: String(selectedOption.id ?? 0),
          tabSid: 'details',
        })}
        avatar={
          <Avatar
            alt={`${selectedOption.first_name ?? 'N/A'} ${
              selectedOption.last_name ?? 'N/A'
            }`}
            src={selectedOption.profile_image}
          />
        }
        label={`${selectedOption.first_name ?? 'N/A'} ${
          selectedOption.last_name ?? 'N/A'
        }`}
        sx={{ cursor: 'pointer' }}
      />
    ) : (
      <Typography variant="body2" fontStyle="italic">
        No cleaner assigned
      </Typography>
    );

  const renderProviderPaySummary = () => (
    <Stack spacing={0.75} sx={{ mt: 1.25 }}>
      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: editable
            ? 'minmax(0, 1fr) auto 52px'
            : 'minmax(0, 1fr) auto',
          columnGap: 1,
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Provider rate
        </Typography>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ justifySelf: 'end', whiteSpace: 'nowrap' }}
        >
          {providerRateDisplay}
        </Typography>
        {editable && (
          <Button
            size="small"
            variant="text"
            aria-label="Change provider rate"
            onClick={handleOpenProviderRateDialog}
            sx={{
              minWidth: 0,
              p: 0,
              fontSize: 12,
              lineHeight: 1.2,
              textTransform: 'none',
              justifySelf: 'end',
            }}
          >
            Change
          </Button>
        )}
      </Stack>
      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: editable
            ? 'minmax(0, 1fr) auto 52px'
            : 'minmax(0, 1fr) auto',
          columnGap: 1,
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Provider income
        </Typography>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ justifySelf: 'end', whiteSpace: 'nowrap' }}
        >
          {providerIncomeValue}
        </Typography>
        {editable && <span />}
      </Stack>
    </Stack>
  );

  const renderProviderResponseSummary = () =>
    selectedOption ? (
      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns:
            editable && !selectedOption.accepted_by_provider
              ? 'minmax(0, 1fr) auto 96px'
              : 'minmax(0, 1fr) auto',
          columnGap: 1,
          alignItems: 'center',
          mt: 1.25,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Provider response
        </Typography>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ justifySelf: 'end', whiteSpace: 'nowrap' }}
        >
          {providerAcceptanceDisplay}
        </Typography>
        {editable && !selectedOption.accepted_by_provider && (
          <LoadingButton
            size="small"
            variant="text"
            aria-label="Mark cleaner accepted"
            onClick={handleMarkProviderAccepted}
            loading={providerAcceptanceUpdateMutation.isLoading}
            sx={{
              minWidth: 0,
              p: 0,
              fontSize: 12,
              lineHeight: 1.2,
              textTransform: 'none',
              justifySelf: 'end',
            }}
          >
            Mark accepted
          </LoadingButton>
        )}
      </Stack>
    ) : null;

  const renderCleanerContent = () => (
    <Stack>
      {renderCleaner()}
      {renderProviderResponseSummary()}
      {renderProviderPaySummary()}
    </Stack>
  );

  return (
    <>
      <VBMCardWrapper>
        {!isEdit && editable && (
          <VBMEditButton onClickButton={() => handleToggleEdit(true)} />
        )}

        <VBMCardTitle title="Cleaner" sx={{ mb: 1 }} />

        {restrictionWarningMessages.length > 0 && (
          <Alert
            severity="warning"
            onClose={() => setRestrictionWarningMessages([])}
            sx={{ mb: 2 }}
          >
            <Stack spacing={0.5}>
              {restrictionWarningMessages.map((message) => (
                <Typography key={message} variant="body2">
                  {message}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {isEdit ? (
          <form onSubmit={handleSubmit}>
            {!isProvidersDataLoading && !isBookingSessionLoading ? (
              <Stack spacing={2}>
                <Autocomplete
                  fullWidth
                  defaultValue={selectedOption ? selectedOption : undefined}
                  filterSelectedOptions
                  options={filteredProviders}
                  onChange={(_e, newValue) =>
                    setSelectedCleanerId(newValue?.id ?? null)
                  }
                  noOptionsText={
                    filteredProviders.length
                      ? 'No such cleaner'
                      : 'No available cleaner'
                  }
                  getOptionLabel={(option: any) =>
                    `${option.first_name ?? 'N/A'} ${option.last_name ?? 'N/A'}`
                  }
                  renderOption={(props, option) => (
                    <MenuItem {...props} key={option.id} value={option.id}>
                      <Chip
                        avatar={
                          <Avatar
                            alt={`${option.first_name ?? 'N/A'} ${
                              option.last_name ?? 'N/A'
                            }`}
                            src={option.profile_image}
                          />
                        }
                        label={`${option.first_name ?? 'N/A'} ${
                          option.last_name ?? 'N/A'
                        }`}
                      />
                    </MenuItem>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search cleaner"
                      placeholder=""
                      fullWidth
                      autoFocus
                      size="small"
                    />
                  )}
                />

                <VBMActionButtons
                  onClickCancel={() => handleToggleEdit(false)}
                  isSubmitting={sessionUpdateMutation.isLoading}
                  disabled={isProvidersDataLoading}
                />
              </Stack>
            ) : (
              <Skeleton
                height={50}
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }}
              />
            )}
          </form>
        ) : (
          <>
            {sessionUpdateMutation.isLoading ? (
              <Skeleton height={50} />
            ) : (
              renderCleanerContent()
            )}
          </>
        )}
      </VBMCardWrapper>

      <Dialog
        open={isProviderRateDialogOpen}
        onClose={handleCloseProviderRateDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Change provider rate</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Provider rate per hour"
            type="text"
            value={providerRateInput}
            onChange={(event) => {
              setProviderRateInput(event.target.value);
              setProviderRateErrorText('');
            }}
            fullWidth
            autoFocus
            inputProps={{ inputMode: 'decimal' }}
            error={Boolean(providerRateErrorText)}
            helperText={
              providerRateErrorText ||
              "Updates this booking session's provider rate."
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseProviderRateDialog}
            disabled={providerRateUpdateMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmitProviderRate}
            loading={providerRateUpdateMutation.isLoading}
          >
            Save provider rate
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
